import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Scale, Shield, Share2, Mail, MapPin, User, Cpu, Database, Map as MapIcon, Layers, Info } from 'lucide-react';
import { useMapStore } from '../store/useMapStore';
import { APP_VERSION } from '../constants';
import { formatVersion } from '../utils/version';
import { useTranslation } from '../i18n/translations';

const LegalModal: React.FC = () => {
  const isOpen = useMapStore(state => state.isLegalModalOpen);
  const activeTab = useMapStore(state => state.legalTab);
  const setLegalModal = useMapStore(state => state.setLegalModal);
  const { t } = useTranslation();

  // Accessibility: Handle Escape key
  React.useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setLegalModal(false);
    };
    if (isOpen) {
      window.addEventListener('keydown', handleEsc);
      // Optional: Prevent scrolling background
      document.body.style.overflow = 'hidden';
    }
    return () => {
      window.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, setLegalModal]);

  if (!isOpen) return null;

  const tabs = [
    { id: 'about', label: t('ABOUT_APP'), icon: Info },
    { id: 'disclaimer', label: t('DISCLAIMER'), icon: Scale },
    { id: 'legal', label: t('LEGAL_PRIVACY'), icon: Shield }
  ];

  const renderContent = () => {
    switch (activeTab) {
      case 'about':
        return (
          <div className="legal-content-body">
            <div className="legal-mascot-container">
              <img src="/branding/vanakkam-mascot.webp" alt="N-Bot" className="legal-mascot" />
            </div>
            <section>
              <h4>{t('DEVELOPER_INFO')}</h4>
              <div className="dev-card">
                <div className="dev-info">
                  <div className="dev-name">
                    <User size={16} />
                    <span>SIVAKAMINATHAN MUTHUSAMY</span>
                  </div>
                  <div className="dev-loc">
                    <MapPin size={16} />
                    <span>Madurai, Tamil Nadu</span>
                  </div>
                </div>
                <div className="dev-links">
                  <a href="https://in.linkedin.com/in/sivakaminathan-muthusamy" target="_blank" rel="noopener noreferrer" className="dev-link">
                    <Share2 size={18} />
                    {t('LINKEDIN')}
                  </a>
                  <a href="mailto:sivakaminathan.muthusamy@gmail.com" className="dev-link">
                    <Mail size={18} />
                    {t('CONTACT')}
                  </a>
                </div>
              </div>
            </section>

            <div className="attributions-grid" style={{ marginTop: '32px' }}>
              <div className="attribution-category">
                <div className="attribution-category-title">
                  <MapIcon size={14} />
                  {t('MAP_INFRASTRUCTURE')}
                </div>
                <div className="attribution-items">
                  <div className="attribution-item">
                    <span className="attribution-name">Leaflet</span>
                    <span className="attribution-desc">Open-source JS library for interactive maps.</span>
                  </div>
                  <div className="attribution-item">
                    <span className="attribution-name">CartoDB Basemaps</span>
                    <span className="attribution-desc">Modern & readable map tiles by Carto.</span>
                  </div>
                  <div className="attribution-item">
                    <span className="attribution-name">OpenStreetMap</span>
                    <span className="attribution-desc">Global map data contributed by OSM users.</span>
                  </div>
                  <div className="attribution-item">
                    <span className="attribution-name">React Leaflet</span>
                    <span className="attribution-desc">React components for Leaflet mapping engine.</span>
                  </div>
                </div>
              </div>

              <div className="attribution-category">
                <div className="attribution-category-title">
                  <Layers size={14} />
                  {t('CORE_FRAMEWORKS')}
                </div>
                <div className="attribution-items">
                  <div className="attribution-item">
                    <span className="attribution-name">React & Vite</span>
                    <span className="attribution-desc">UI components and modern build tooling.</span>
                  </div>
                  <div className="attribution-item">
                    <span className="attribution-name">Zustand</span>
                    <span className="attribution-desc">Application state management.</span>
                  </div>
                  <div className="attribution-item">
                    <span className="attribution-name">Framer Motion</span>
                    <span className="attribution-desc">Premium animations and gestures.</span>
                  </div>
                  <div className="attribution-item">
                    <span className="attribution-name">TypeScript</span>
                    <span className="attribution-desc">Type-safe GIS development.</span>
                  </div>
                </div>
              </div>

              <div className="attribution-category">
                <div className="attribution-category-title">
                  <Cpu size={14} />
                  {t('GIS_ENGINE')}
                </div>
                <div className="attribution-items">
                  <div className="attribution-item">
                    <span className="attribution-name">Turf.js</span>
                    <span className="attribution-desc">Advanced geospatial analysis and logic.</span>
                  </div>
                  <div className="attribution-item">
                    <span className="attribution-name">RBush</span>
                    <span className="attribution-desc">Spatial indexing for 2D geometry.</span>
                  </div>
                  <div className="attribution-item">
                    <span className="attribution-name">TopoJSON</span>
                    <span className="attribution-desc">Topology-preserving geographic data parsing.</span>
                  </div>
                  <div className="attribution-item">
                    <span className="attribution-name">IDB</span>
                    <span className="attribution-desc">Persistent local GIS data caching.</span>
                  </div>
                </div>
              </div>

              <div className="attribution-category">
                <div className="attribution-category-title">
                  <Database size={14} />
                  {t('DATA_SOURCES_CREDITS')}
                </div>
                <div className="attribution-items">
                  <div className="attribution-item">
                    <span className="attribution-name">GIS TN (TNGIS)</span>
                    <span className="attribution-desc">TN Government's boundary and GIS portal.</span>
                  </div>
                  <div className="attribution-item">
                    <span className="attribution-name">ECI & CEO Tamil Nadu</span>
                    <span className="attribution-desc">Electoral constituency and result data.</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      case 'disclaimer':
        return (
          <div className="legal-content-body">
            <section>
              <h4>{t('NON_GOVT_TITLE')}</h4>
              <p>
                <strong>NammaMap</strong> {t('NON_GOVT_DESC')}
              </p>
              <p>
                {t('DATA_SOURCE_DESC')}
              </p>
            </section>

            <section>
              <h4>{t('ELECTION_DATA_TITLE')}</h4>
              <p>
                {t('ELECTION_DATA_DESC')}
              </p>
            </section>

            <section>
              <h4>{t('ACCURACY_DATA')}</h4>
              <p>
                {t('VERIFY_OFFICIAL')}
              </p>
            </section>
          </div>
        );
      case 'legal':
        return (
          <div className="legal-content-body">
            <section>
              <h4>{t('ZERO_DATA_TITLE')}</h4>
              <p>
                {t('ZERO_DATA_DESC')}
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '12px' }}>
                <p><strong>{t('LOCATION_ACCESS')}:</strong> {t('LOCATION_PRIVACY')}</p>
                <p><strong>{t('CACHING')}:</strong> {t('CACHING_DESC')}</p>
                <p><strong>{t('INTELLECTUAL_PROPERTY')}:</strong> {t('IP_DESC')}</p>
              </div>
            </section>
            <section>
              <h4>{t('LIMITATION_LIABILITY')}</h4>
              <p>
                {t('LIABILITY_DESC')}
              </p>
            </section>
            <section>
              <h4>{t('THIRD_PARTY_SERVICES')}</h4>
              <p>
                {t('THIRD_PARTY_DESC')}
              </p>
            </section>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="modal-overlay"
        onClick={() => setLegalModal(false)}
      >
        <motion.div
          initial={{ scale: 0.95, y: 20, opacity: 0 }}
          animate={{ scale: 1, y: 0, opacity: 1 }}
          exit={{ scale: 0.95, y: 20, opacity: 0 }}
          className="modal-content glass-heavy legal-modal"
          onClick={e => e.stopPropagation()}
        >
          <div className="modal-header">
            <div className="modal-title-group">
              <Scale size={20} className="text-accent" />
              <h3>{t('ABOUT_LEGAL')}</h3>
            </div>
            <button className="modal-close" onClick={() => setLegalModal(false)}>
              <X size={20} />
            </button>
          </div>

          <div className="legal-tabs">
            {tabs.map(tab => (
              <button
                key={tab.id}
                className={`legal-tab-btn ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setLegalModal(true, tab.id as any)}
              >
                <tab.icon size={16} style={{ flexShrink: 0 }} />
                <span>{tab.label}</span>
              </button>
            ))}
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, x: 10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -10 }}
              transition={{ duration: 0.2 }}
              className="modal-body legal-modal-body"
            >
              {renderContent()}
            </motion.div>
          </AnimatePresence>

          <div className="modal-footer">
            <div className="modal-footer-version">
              {t('BUILD_VERSION')}: <span>v{formatVersion(APP_VERSION)}</span>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default LegalModal;
